import { ActionButton } from '@/components/common/ActionButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { MOTION } from '@/components/common/motion';
import { CHIP } from '@/components/common/surfaces';
import { AssemblyDrawingViewport } from '@/components/dialogs/assemblyDrawing/AssemblyDrawingViewport';
import { buildDrawingLayout } from '@/components/dialogs/assemblyDrawing/drawingLayout';
import { AssemblyPropertyPanel } from '@/components/dialogs/assemblyDrawing/AssemblyPropertyPanel';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { useAssemblyDrawing } from '@/hooks/useAssemblyDrawing';
import type { DrawingErrorKind } from '@/hooks/useAssemblyDrawing';
import { ArrowClockwise, ArrowsIn, ArrowsOut, CircleNotch, DownloadSimple, PencilRuler } from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AssemblyDrawingDialogProps {
  visible: boolean;
  /** Tool whose assemblies are shown; null while the dialog is closed. */
  toolId: string | null;
  toolName: string;
  onClose: () => void;
}

/** How tall a stand-in has to be before the sheet below it stops looking collapsed. */
const PANEL_FRAME = 'flex h-full min-h-[320px] flex-col items-center justify-center gap-4 px-6 py-12';

/**
 * The NC selector chip.
 *
 * The active one repeats the chip's geometry rather than overriding `CHIP`'s colours: two
 * background utilities on one element are settled by the order Tailwind emitted them in, not
 * by the order they appear here, so the selected state would be a coin toss.
 */
const CHIP_INTERACTION = `${MOTION} active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;
const SELECTOR_CHIP = `${CHIP} hover:bg-gray-200 ${CHIP_INTERACTION}`;
const SELECTOR_CHIP_ACTIVE = `inline-flex items-center rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white ${CHIP_INTERACTION}`;

/** Centred stand-in while a drawing or the assembly list is on its way. */
const LoadingPanel: FC<{ label: string }> = ({ label }) => (
  <div className={PANEL_FRAME}>
    <CircleNotch size={22} className="animate-spin text-gray-400" aria-hidden="true" />
    <p className="m-0 text-sm text-gray-500">{label}</p>
  </div>
);

interface ErrorPanelProps {
  message: string;
  detail?: string;
  action?: ReactNode;
}

/** Centred failure notice: red is spent on the one thing that went wrong, and nowhere else. */
const ErrorPanel: FC<ErrorPanelProps> = ({ message, detail, action }) => (
  <div className={PANEL_FRAME}>
    <div className="w-full max-w-md">
      <ErrorMessage message={message} />
    </div>
    {detail ? <p className="m-0 max-w-md text-center text-sm text-gray-500 [text-wrap:pretty]">{detail}</p> : null}
    {action}
  </div>
);

/**
 * Modal showing a tool's assembly as a drawing sheet, split in two.
 *
 * Left is the property panel: real HTML at a fixed width, with its own scrollbar and
 * selectable text, so it neither zooms nor resizes with the drawing. Right is the tool
 * itself, drawn natively as SVG from `GET /assemblies/{id}/drawing-data` and zoomable,
 * pannable and resettable. Nothing about the drawing is an image any more — only the
 * "download PNG" action still goes back to the backend renderer.
 *
 * Scroll containment is deliberate and has two halves: `blockScroll` freezes the page
 * behind the modal, and the drawing frame swallows the wheel entirely (see
 * `AssemblyDrawingViewport`). The property column scrolls, but only itself.
 *
 * Tools with two assemblies get a chip selector that refetches on switch; tools with none
 * get an explicit empty state instead of a blank frame.
 */
export const AssemblyDrawingDialog: FC<AssemblyDrawingDialogProps> = ({ visible, toolId, toolName, onClose }) => {
  const { t } = useTranslation();
  const {
    assemblies,
    selectedAssembly,
    selectAssembly,
    isListLoading,
    hasListError,
    retryList,
    drawingSpec,
    isDrawingLoading,
    drawingError,
    retryDrawing,
    downloadPng,
    isDownloading,
  } = useAssemblyDrawing(visible ? toolId : null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const layout = useMemo(() => (drawingSpec ? buildDrawingLayout(drawingSpec) : null), [drawingSpec]);

  const drawingErrorText = (kind: DrawingErrorKind): { title: string; detail: string } => {
    if (kind === 'undrawable') {
      return {
        title: t('toolManagement.assemblyDrawingUndrawable'),
        detail: t('toolManagement.assemblyDrawingUndrawableHint'),
      };
    }
    if (kind === 'notFound') {
      return {
        title: t('toolManagement.assemblyDrawingNotFound'),
        detail: t('toolManagement.assemblyDrawingNotFoundHint'),
      };
    }
    return { title: t('toolManagement.assemblyDrawingError'), detail: t('toolManagement.assemblyDrawingErrorHint') };
  };

  const title =
    selectedAssembly?.nc_number != null
      ? t('toolManagement.assemblyDrawingTitle', { ncNumber: selectedAssembly.nc_number, toolName })
      : t('toolManagement.assemblyDrawingTitleFallback', { toolName });

  const fullscreenLabel = isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen');

  const dialogHeader = (
    <div className="flex w-full items-center justify-between gap-3 pr-2">
      <span className="truncate text-lg font-semibold">{title}</span>
      <ActionButton
        variant="ghost"
        iconOnly={true}
        icon={isFullscreen ? <ArrowsIn size={16} aria-hidden="true" /> : <ArrowsOut size={16} aria-hidden="true" />}
        onClick={() => setIsFullscreen((previous) => !previous)}
        title={fullscreenLabel}
        aria-label={fullscreenLabel}
      />
    </div>
  );

  const retryButton = (onRetry: () => void) => (
    <ActionButton icon={<ArrowClockwise size={16} aria-hidden="true" />} onClick={onRetry}>
      {t('toolManagement.assemblyDrawingRetry')}
    </ActionButton>
  );

  /** The split sheet, or the one status message that stands in for both halves. */
  const renderSheet = () => {
    if (isDrawingLoading) {
      return <LoadingPanel label={t('toolManagement.assemblyDrawingLoading')} />;
    }
    if (drawingError) {
      const { title: errorTitle, detail } = drawingErrorText(drawingError);
      // A drawing the backend cannot produce is not a failure to report in red: the sheet is
      // simply not there, which is what an empty state says.
      if (drawingError !== 'generic') {
        return (
          <div className="flex h-full min-h-[320px] items-center justify-center">
            <EmptyState icon={<PencilRuler size={28} aria-hidden="true" />} title={errorTitle} message={detail} />
          </div>
        );
      }
      return <ErrorPanel message={errorTitle} detail={detail} action={retryButton(retryDrawing)} />;
    }
    if (!drawingSpec || !layout) return null;
    return (
      <div className="flex h-full min-h-0 gap-4">
        {/* Fixed 340 px: the property column is a reading column, not a resizable pane. */}
        <AssemblyPropertyPanel panel={drawingSpec.panel} className="h-full w-[340px] shrink-0" />
        <AssemblyDrawingViewport
          spec={drawingSpec}
          layout={layout}
          title={t('toolManagement.assemblyDrawingAlt', { assemblyId: drawingSpec.meta.assembly_id })}
          className="h-full min-w-0 flex-1"
        />
      </div>
    );
  };

  const renderBody = () => {
    if (isListLoading) {
      return <LoadingPanel label={t('toolManagement.assemblyDrawingLoadingList')} />;
    }
    if (hasListError) {
      return (
        <ErrorPanel
          message={t('toolManagement.assemblyDrawingListError')}
          detail={t('toolManagement.assemblyDrawingErrorHint')}
          action={retryButton(retryList)}
        />
      );
    }
    if (assemblies.length === 0) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center">
          <EmptyState
            icon={<PencilRuler size={28} aria-hidden="true" />}
            title={t('toolManagement.assemblyDrawingEmpty')}
            message={t('toolManagement.assemblyDrawingEmptyHint')}
          />
        </div>
      );
    }

    return (
      <div className={`flex flex-col gap-4 ${isFullscreen ? 'h-full min-h-0' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {assemblies.length > 1 ? (
            <div
              className="flex flex-wrap items-center gap-2"
              role="group"
              aria-label={t('toolManagement.assemblyDrawingSelectLabel')}
            >
              {assemblies.map((assembly) => {
                const isActive = assembly.id === selectedAssembly?.id;
                return (
                  <button
                    key={assembly.id}
                    type="button"
                    onClick={() => selectAssembly(assembly.id)}
                    aria-pressed={isActive}
                    className={isActive ? SELECTOR_CHIP_ACTIVE : SELECTOR_CHIP}
                  >
                    {assembly.nc_number != null
                      ? t('toolManagement.assemblyDrawingNcLabel', { ncNumber: assembly.nc_number })
                      : assembly.assembly_id}
                  </button>
                );
              })}
            </div>
          ) : (
            <span className="text-sm text-gray-500">{t('toolManagement.assemblyDrawingSingleAssembly')}</span>
          )}

          <ActionButton
            icon={<DownloadSimple size={16} aria-hidden="true" />}
            onClick={() => {
              downloadPng();
            }}
            disabled={isDownloading || !drawingSpec}
            loading={isDownloading}
          >
            {t('toolManagement.assemblyDrawingDownloadPng')}
          </ActionButton>
        </div>

        {selectedAssembly ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`${CHIP} tabular-nums`}>{selectedAssembly.assembly_id}</span>
            {selectedAssembly.assembly_comment ? (
              <span className="text-sm text-gray-500">{selectedAssembly.assembly_comment}</span>
            ) : null}
          </div>
        ) : null}

        <div className={isFullscreen ? 'min-h-0 flex-1' : 'h-[62vh] min-h-[380px]'}>{renderSheet()}</div>
      </div>
    );
  };

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onClose}
      modal={true}
      dismissableMask={true}
      blockScroll={true}
      className={`w-full ${isFullscreen ? 'h-full max-w-full' : 'max-w-6xl'}`}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        ...TOOL_MANAGEMENT_DIALOG_PT,
        root: {
          className: `${TOOL_MANAGEMENT_DIALOG_PT.root.className} ${
            isFullscreen ? 'fixed inset-0 h-full !max-h-full' : ''
          }`,
        },
        content: {
          className: `${TOOL_MANAGEMENT_DIALOG_PT.content.className} overscroll-contain ${
            isFullscreen ? 'flex h-full flex-col' : ''
          }`,
        },
      }}
    >
      {renderBody()}
    </Dialog>
  );
};
